import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApis } from '../../../../api/useApiClient';
import { useNotification } from "../../../../context/NotificationContext";

import { Grid, Button, Box, LinearProgress } from "@mui/material";

import ConfirmIcon from '@mui/icons-material/Check';
import CancelIcon from "@mui/icons-material/Clear";

import ConfirmationDialog from '../../../Common/ConfirmationDialog';

export default function POSCheckout({ workOrder = {}, disabled = false }) {

    const { tenantApi } = useApis();
    const { showNotification } = useNotification();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(false);
    const [workOrderState, setWorkOrderState] = useState({});
    const [confirmCancelDialogOpen, setConfirmCancelDialogOpen] = useState(false);
    
    useEffect(() => {
        setWorkOrderState(workOrder);
    }, [workOrder]);


    async function handleCheckout() {
        setLoading(true);
        try {
            // Single API call that closes work order + creates invoice in one transaction
            const checkoutRes = await tenantApi(`/tenants/work-orders/${workOrderState.id}/checkout`, {
                method: 'POST'
            });

            showNotification('Order completed and invoice created', 'success');
            
            // Navigate to the invoice for payment
            navigate(`/sales/invoices/${checkoutRes.data.invoice.id}`, { replace: true });
        } catch (e) {
            console.error(e);
            if (e.message)
                showNotification(e.message, "error");
            else
                showNotification(`Failed to complete checkout. Please try again later.`, "error");
        } finally {
            setLoading(false);
        }
    }

    async function handleCancelClick() {
        setConfirmCancelDialogOpen(true);
    }

    async function cancelWorkOrder() {
        setLoading(true);
        console.log('Cancelling work order', workOrderState.id);
        try {
            await tenantApi(`/tenants/work-orders/${workOrderState.id}/cancel`, {
                method: 'POST',
            });
            showNotification('Order cancelled successfully', 'success');
            navigate('/sales/pos/start', { replace: true });
        } catch (e) {
            console.error(e);
            if (e.message)
                showNotification(e.message, "error");
            else
                showNotification(`Failed to cancel the order. Please try again later.`, "error");
        } finally {
            setLoading(false);
            setConfirmCancelDialogOpen(false);
        }
    }

    const isCheckoutDisabled = disabled || !workOrderState.id || ['INVOICED', 'CANCELED'].includes(workOrderState.status) || loading;

    return (
        <>
            <Box sx={{ p: 2 }}>
                <Grid container spacing={2} justifyContent={{ xs: 'stretch', md: 'flex-end' }}>
                    {/* Checkout button - appears first on mobile, last on desktop */}
                    <Grid item xs={12} md="auto" order={{ xs: 1, md: 2 }}>
                        <Button
                            fullWidth
                            variant="contained"
                            color="success"
                            startIcon={<ConfirmIcon />}
                            onClick={handleCheckout}
                            disabled={isCheckoutDisabled}
                            sx={{ minWidth: { md: 180 } }}
                        >
                            Complete & Pay
                        </Button>
                    </Grid>
                    {/* Cancel button - appears second on mobile, first on desktop */}
                    <Grid item xs={12} md="auto" order={{ xs: 2, md: 1 }}>
                        <Button
                            fullWidth
                            variant="outlined"
                            color="error"
                            startIcon={<CancelIcon />}
                            onClick={handleCancelClick}
                            disabled={isCheckoutDisabled}
                            sx={{ minWidth: { md: 150 } }}
                        >
                            Cancel Order
                        </Button>
                    </Grid>
                </Grid>
                {loading &&
                    <Box sx={{ width: '100%', mt: 2 }}>
                        <LinearProgress />
                    </Box>
                }
            </Box>

            {/* Cancel Work Order Confirmation Dialog */}
            <ConfirmationDialog
                open={confirmCancelDialogOpen}
                title="Cancel Order"
                message={`Are you sure you want to cancel this order? All reserved inventory will be released.`}
                confirmText="Yes, Cancel"
                cancelText="No, Keep It"
                confirmColor="error"
                onConfirm={cancelWorkOrder}
                onCancel={() => setConfirmCancelDialogOpen(false)}
            />
        </>
    );
}
